/**
 * AI prompts for PowerPoint template slots.
 *
 * A slot is a `{{name}}` placeholder in a slide's HTML. Its AI prompt is the `description` of
 * the matching row in the template's `variableCatalog`, which the worker sends to the model
 * when it fills the slot. The editor keeps prompts in a `{ [name]: prompt }` map: a name with
 * a non-blank entry uses that text, any other name falls back to the heuristic description
 * from {@link buildVariableCatalog}.
 */
import { buildVariableCatalog, mergeVariableCatalogDescriptions } from '../../utils/presentationAiArtifacts';

/** Longest prompt kept on save (mergeVariableCatalogDescriptions trims to this). */
export const MAX_PROMPT_LENGTH = 900;

/**
 * Every `{{name}}` in slide HTML, in order of first appearance (same pattern as the catalog).
 * @param {string} html - Slide HTML.
 * @returns {string[]} Unique names.
 */
export function variableNamesInHtml(html) {
  const re = /\{\{\s*([a-zA-Z0-9_.-]+)\s*\}\}/g;
  const names = [];
  const seen = new Set();
  let match;
  while ((match = re.exec(String(html || ''))) !== null) {
    if (!seen.has(match[1])) {
      seen.add(match[1]);
      names.push(match[1]);
    }
  }
  return names;
}

/**
 * The prompt map stored in a saved catalog.
 * @param {unknown} catalog - `variableCatalog` from template content.
 * @returns {Record<string, string>} Prompt per name (rows without a name or text are skipped).
 */
export function promptsFromCatalog(catalog) {
  const prompts = {};
  if (!Array.isArray(catalog)) return prompts;
  for (const row of catalog) {
    if (!row || typeof row.name !== 'string' || !row.name) continue;
    if (typeof row.description !== 'string' || !row.description.trim()) continue;
    if (Object.prototype.hasOwnProperty.call(prompts, row.name)) continue;
    prompts[row.name] = row.description.slice(0, MAX_PROMPT_LENGTH);
  }
  return prompts;
}

/**
 * Whether a prompt map entry has text of its own.
 * @param {Record<string, string>} prompts - Prompt map.
 * @param {string} name - Variable name.
 * @returns {boolean} True when the entry is non-blank.
 */
function hasPrompt(prompts, name) {
  return Object.prototype.hasOwnProperty.call(prompts, name)
    && typeof prompts[name] === 'string'
    && prompts[name].trim() !== '';
}

/**
 * Heuristic prompt per variable name (what an empty prompt falls back to).
 * @param {{ content: string, aiNotes?: object }[]} slides - Slides.
 * @returns {Record<string, string>} Suggested prompt per name.
 */
export function suggestedPrompts(slides) {
  const out = {};
  for (const row of buildVariableCatalog(slides)) out[row.name] = row.description;
  return out;
}

/**
 * The catalog saved with the template: one row per variable still on a slide, using the
 * edited prompt when there is one and the heuristic description otherwise.
 * @param {{ content: string, aiNotes?: object }[]} slides - Slides.
 * @param {Record<string, string>} prompts - Prompt map.
 * @returns {{ name: string, description: string, slideNumber?: number }[]} Catalog rows.
 */
export function buildPromptCatalog(slides, prompts) {
  const heuristic = buildVariableCatalog(slides);
  const rows = Object.keys(prompts || {})
    .filter((name) => hasPrompt(prompts, name))
    .map((name) => ({ name, description: prompts[name] }));
  return mergeVariableCatalogDescriptions(rows, heuristic);
}

/**
 * Slide numbers (1-based) each variable appears on.
 * @param {{ content: string }[]} slides - Slides.
 * @returns {Record<string, number[]>} Slide numbers per name.
 */
export function slidesByVariable(slides) {
  const out = {};
  (slides || []).forEach((slide, index) => {
    for (const name of variableNamesInHtml(slide?.content)) {
      if (!out[name]) out[name] = [];
      out[name].push(index + 1);
    }
  });
  return out;
}

/**
 * Keep a prompt when its `{{name}}` is renamed in one slide.
 *
 * Renaming in the editor happens a keystroke at a time and can pass through text that is not
 * a placeholder at all (`{{goals}` while the braces are retyped). So a name that disappears
 * from this slide (and is not used on any other) is remembered as pending; when exactly one
 * new name without a prompt appears on the slide while exactly one prompt is pending, the
 * prompt moves to the new name. Anything else (several names added at once, pasting) is
 * ambiguous and closes the window, so prompts are never moved by guesswork.
 *
 * @param {object} args - Arguments.
 * @param {string} args.prevHtml - Slide HTML before the change.
 * @param {string} args.nextHtml - Slide HTML after the change.
 * @param {Set<string>} args.namesElsewhere - Names used on the other slides.
 * @param {Record<string, string>} args.prompts - Prompt map.
 * @param {string[]} [args.pending] - Names this slide lost in earlier edits.
 * @returns {{ prompts: Record<string, string>, pending: string[] }} Updated map (same object when unchanged) and pending names.
 */
export function carryPromptsAcrossRename({ prevHtml, nextHtml, namesElsewhere, prompts, pending = [] }) {
  const before = variableNamesInHtml(prevHtml);
  const after = variableNamesInHtml(nextHtml);
  const afterSet = new Set(after);
  const beforeSet = new Set(before);
  const elsewhere = namesElsewhere || new Set();

  const added = after.filter((name) => !beforeSet.has(name) && !elsewhere.has(name));
  const orphans = Array.from(new Set([...pending, ...before.filter((name) => !afterSet.has(name))]))
    .filter((name) => !afterSet.has(name) && !elsewhere.has(name) && hasPrompt(prompts, name));

  if (added.length === 1 && orphans.length === 1 && !hasPrompt(prompts, added[0])) {
    const next = { ...prompts, [added[0]]: prompts[orphans[0]] };
    delete next[orphans[0]];
    return { prompts: next, pending: [] };
  }
  return { prompts, pending: added.length > 0 ? [] : orphans };
}
